import React from 'react';

import {assertAndContinue} from 'Asserts';
import {Sequence as BioJSSequence} from 'biojs/Sequence';
import DataConnector from 'connect/DataConnector';
import {Strand} from 'constants/MolbioConstants';
import {MatchSite} from 'constants/SequenceConstants';
import {OptimizeCodonsResult} from 'molbio/codon-optimization/CodonOptimizationConstants';
import CodonOptimizationTabPreview from 'molbio/codon-optimization/CodonOptimizationTabPreview';
import CodonOptimizationTabSelection from 'molbio/codon-optimization/CodonOptimizationTabSelection';
import {CodonOptimizationTabsSelector} from 'molbio/codon-optimization/reduceAllCodonOptimizationTabs';

class CodonOptimizationTabConnector extends DataConnector<CodonOptimizationTabProps, EnhancedProps | null> {
  enhanceProps(): EnhancedProps | null {
    const selector = new CodonOptimizationTabsSelector(this.reduxState());
    const state = selector.getState(this.props.tabId);
    if (!state) {
      return null;
    }
    return {
      isShowingOptions: state.isShowingOptions,
      isLoading: state.isLoading,
      result: state.result,
    };
  }
}

export interface CodonOptimizationTabProps {
  onClose: () => void;
  tabId: string;
  startIndex: number;
  onReplaceSequence:
    | ((start: number, end: number, bases: string, options: {callback: () => void}) => void)
    | null;
  name: string;
  onSetSelection: (start: number, end: number) => void;
  currentSelection: {
    start: number;
    end: number | null;
  };
  seqLength: number;
  sequenceId: string;
  circular: boolean;
  isRegionEditable: boolean;
  isBackTranslation: boolean;
  strand: Strand;
  seqEnd: number | null;
  matchSites: Array<MatchSite> | null;
  createFromSelection: ((bases: string, name: string) => BioJSSequence) | null;
}

interface EnhancedProps {
  isShowingOptions: boolean;
  isLoading: boolean;
  result: OptimizeCodonsResult | null;
}

const CodonOptimizationTab = (
  props: CodonOptimizationTabProps & Partial<EnhancedProps>
): JSX.Element | null => {
  // Tab state has been removed (e.g., after close action) - render nothing
  if (props.isShowingOptions === undefined) {
    return null;
  }
  if (props.isShowingOptions) {
    return (
      <CodonOptimizationTabSelection
        onClose={props.onClose}
        tabId={props.tabId}
        sequenceId={props.sequenceId}
        startIndex={props.startIndex}
        onSetSelection={props.onSetSelection}
        currentSelection={props.currentSelection}
        seqLength={props.seqLength}
        isBackTranslation={props.isBackTranslation}
        strand={props.strand}
        matchSites={props.matchSites}
      />
    );
  } else if (props.result) {
    return (
      <CodonOptimizationTabPreview
        result={props.result}
        onClose={props.onClose}
        name={props.name}
        onReplaceSequence={props.onReplaceSequence}
        startIndex={props.startIndex}
        tabId={props.tabId}
        sequenceId={props.sequenceId}
        circular={props.circular}
        seqLength={props.seqLength}
        isRegionEditable={props.isRegionEditable}
        isBackTranslation={props.isBackTranslation}
        strand={props.strand}
        seqEnd={props.seqEnd}
        createFromSelection={props.createFromSelection}
      />
    );
  } else {
    assertAndContinue(false, 'Expected isShowingOptions or result to be set.');
    return null;
  }
};

export default CodonOptimizationTabConnector.connect(CodonOptimizationTab);
